/**
 * Eligibility Rules - Shared types for per-user eligibility system.
 *
 * Eligibility definitions are stored as a document in the settings container.
 * User overrides are stored as eligibilityMap on the User document.
 */

/**
 * When a counted eligibility's `used` returns to zero.
 *  - `day` / `week` / `month` — calendar windows, anchored to IST midnight.
 *  - `plan` — the winning grant's own period: a Plus month bought on the 25th
 *    counts from the 25th to its end, not to the 1st. Set only on a plan
 *    (`SubscriptionDefinition.resetPeriods`), never on a definition.
 *  - `none` — lifetime.
 */
export type ResetPeriod = 'none' | 'day' | 'week' | 'month' | 'plan';

export interface EligibilityDefinition {
    name: string;            // unique key, e.g. 'voice_conversations'
    displayName: string;     // e.g. 'Voice Conversations'
    description: string;     // what this eligibility controls
    enableValues: string[];  // e.g. ['enabled', 'disabled']
    defaultValue: string;    // value when user has no override
    resetPeriod?: ResetPeriod;  // quota reset window: 'day' | 'week' | 'month' | 'none' (default: lifetime)
}

export interface EligibilityDefinitionsDocument {
    id: 'eligibility-definitions';  // document ID in settings container
    eligibilities: EligibilityDefinition[];
    updatedAt: string;
}

// Subscription system types

/** The three plans of the billing model (docs/FeatureDesigns/billing). */
export type PlanTier = 'basic' | 'plus' | 'pro';
export const PLAN_TIERS: readonly PlanTier[] = ['basic', 'plus', 'pro'];

/** Monthly prices in paise, GST-inclusive. */
export interface PlanPrices {
    student: number;            // one student, one full month
    teacherPerStudent: number;  // one student, one full month, bought by a teacher
}

export interface SubscriptionDefinition {
    id: string;                    // unique slug, e.g. 'pro-plan'
    name: string;                  // display name, e.g. 'Pro Plan'
    description: string;
    eligibilities: Record<string, string>;  // eligibilityMap key → value
    /**
     * Per-plan reset window for a counted eligibility, overriding the
     * definition's `resetPeriod`. Lets Basic/Plus/Pro count AI checks per day
     * while guests on the same eligibility keep a lifetime allowance.
     */
    resetPeriods?: Record<string, ResetPeriod>;
    /** Set on the billing plans `basic`, `plus`, `pro`; absent on legacy plans. */
    tier?: PlanTier;
    /** Can be bought on the subscription page. */
    purchasable?: boolean;
    pricePaise?: PlanPrices;
    /** Devices signed in at once (enforced from billing phase 5). */
    deviceLimit?: number;
    createdAt: string;
    updatedAt: string;
}

export interface SubscriptionDefinitionsDocument {
    id: 'subscription-definitions';
    subscriptions: SubscriptionDefinition[];
    updatedAt: string;
}

export interface UserSubscription {
    id: string;                    // auto-generated UUID
    userId: string;
    subscriptionId: string;        // references SubscriptionDefinition.id
    subscriptionName: string;      // denormalized for display
    startDate: string;             // ISO date
    endDate: string;               // ISO date
    status: 'active' | 'cancelled';
    assignedBy: string;            // admin user who assigned
    createdAt: string;
    subjects: string[];            // subject scoping (empty array = all subjects)
    /** Where the grant came from. Absent on grants written before billing. */
    source?: GrantSource;
    /** The order that created it — makes fulfilment idempotent. */
    orderId?: string;
    /** Teacher userId for a 'teacher' grant, tenantId for a 'school' grant. */
    purchasedBy?: string;
    /** Trial only: the grant ends once this many study days have passed since it began. */
    endsAfterStudyDays?: number;
    /** Trial only: the user's `studyDayCount` when the trial began. */
    trialStartStudyDayCount?: number;
}

export type GrantSource =
    | 'registration' | 'trial' | 'purchase' | 'teacher' | 'school'
    | 'migration' | 'admin' | 'guest' | 'iap';
